<script setup>
import { ref, computed, onMounted } from 'vue'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Layers } from 'lucide-vue-next'

const props = defineProps({
  orgId: {
    type: [String, Number],
    required: true
  },
  selectedLayer: {
    type: Object,
    default: null
  },
  excludeLayers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['select'])

const layers = ref([])
const loading = ref(false)
const selectedLayerCode = ref(null)

// Computed filtered layers excluding already connected ones
const filteredLayers = computed(() => {
  if (!props.excludeLayers || props.excludeLayers.length === 0) {
    return layers.value
  }
  return layers.value.filter(layer =>
    !props.excludeLayers.includes(layer.code)
  )
})

// Load layers for the organization
const loadLayers = async () => {
  if (!props.orgId) return

  loading.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/layers/all`
    })

    // Filter for collected_data layers only
    layers.value = (response.data || []).filter(layer => layer.type === 4) // 4 = collected_data

    // Auto-select first layer if none selected
    if (layers.value.length > 0 && !props.selectedLayer) {
      handleSelect(layers.value[0].code)
    }
  } catch (error) {
    console.error('Error loading layers:', error)
    toast({
      title: 'Error loading layers',
      description: 'Failed to load available layers',
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}

const handleSelect = (layerCode) => {
  const layer = layers.value.find(l => l.code === layerCode)
  if (layer) {
    selectedLayerCode.value = layerCode
    emit('select', layer)
  }
}

onMounted(() => {
  loadLayers()
})
</script>

<template>
  <div class="w-full">
    <Select v-model="selectedLayerCode" @update:modelValue="handleSelect">
      <SelectTrigger :disabled="loading || filteredLayers.length === 0">
        <Layers class="h-4 w-4 mr-2" />
        <SelectValue :placeholder="loading ? 'Loading layers...' : (filteredLayers.length === 0 ? 'No available layers' : 'Select a layer')" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Available Layers</SelectLabel>
          <SelectItem
            v-for="layer in filteredLayers"
            :key="layer.code"
            :value="layer.code"
          >
            {{ layer.name || `Layer ${layer.id}` }}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
</template>
